/**
 * ScheduledExportsList Component
 * 
 * Displays a table of scheduled exports with management actions.
 * Can be used standalone or within a modal.
 */

import React, { useState } from "react";
import { Table, Button, Badge, Spinner, Alert, Modal } from "react-bootstrap";
import { PencilSquare, Trash, PauseCircle, PlayCircle } from "react-bootstrap-icons";
import styled from "styled-components";
import moment from "moment";

import { ExportSchedule, ExportScheduleStatus } from "../../services/export/types";
import Logger from "../../utils/Logger";

const StyledTable = styled(Table)`
    th {
        font-weight: 600;
        white-space: nowrap;
    }
    
    td {
        vertical-align: middle;
    }
    
    .actions-cell {
        white-space: nowrap;
    }
    
    .frequency-badge {
        text-transform: capitalize;
    }
`;

const ActionButton = styled(Button)`
    padding: 4px 8px;
    margin-right: 4px;
    
    &:last-child {
        margin-right: 0;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
    
    h5 {
        margin-bottom: 8px;
    }
`;

const RecipientsList = styled.div`
    max-width: 200px;
    
    .more-recipients {
        color: #6c757d;
        font-size: 0.85rem;
    }
`;

export type ScheduledExportsListProps = {
    /** List of schedules to display */
    schedules: ExportSchedule[];
    /** Whether data is loading */
    isLoading?: boolean;
    /** Error message to display */
    error?: string | null;
    /** Callback to edit a schedule */
    onEdit?: (schedule: ExportSchedule) => void;
    /** Callback to delete a schedule */
    onDelete?: (scheduleId: string) => Promise<void>;
    /** Callback to pause/resume a schedule */
    onToggleStatus?: (scheduleId: string, newStatus: ExportScheduleStatus) => Promise<void>;
    /** Maximum recipients to show before truncating */
    maxRecipientsShown?: number;
};

/**
 * Get status badge variant
 */
function getStatusVariant(status: ExportScheduleStatus): string {
    switch (status) {
        case "active":
            return "success";
        case "paused":
            return "warning";
        case "deleted":
            return "danger";
        default:
            return "secondary";
    }
}

/**
 * Format frequency for display
 */
function formatFrequency(schedule: ExportSchedule): string {
    switch (schedule.frequency) {
        case "daily":
            return `Daily at ${schedule.timeUtc} UTC`;
        case "weekly": {
            const day = schedule.dayOfWeek ? schedule.dayOfWeek.charAt(0).toUpperCase() + schedule.dayOfWeek.slice(1) : "Monday";
            return `Weekly on ${day} at ${schedule.timeUtc} UTC`;
        }
        case "monthly": {
            const dayNum = schedule.dayOfMonth || 1;
            return `Monthly on day ${dayNum} at ${schedule.timeUtc} UTC`;
        }
        default:
            return schedule.frequency;
    }
}

/**
 * Table component for managing scheduled exports
 */
const ScheduledExportsList: React.FC<ScheduledExportsListProps> = ({
    schedules,
    isLoading = false,
    error,
    onEdit,
    onDelete,
    onToggleStatus,
    maxRecipientsShown = 2
}) => {
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const handleDelete = async (scheduleId: string) => {
        if (!onDelete) return;
        
        setConfirmDeleteId(null);
        setDeletingId(scheduleId);

        try {
            await onDelete(scheduleId);
        } catch (err) {
            Logger.log("error", "ScheduledExportsList", "Failed to delete schedule", err);
        } finally {
            setDeletingId(null);
        }
    };

    const handleToggleStatus = async (schedule: ExportSchedule) => {
        if (!onToggleStatus) return;

        const newStatus: ExportScheduleStatus = schedule.status === "active" ? "paused" : "active";
        setTogglingId(schedule.id);

        try {
            await onToggleStatus(schedule.id, newStatus);
        } catch (err) {
            Logger.log("error", "ScheduledExportsList", "Failed to toggle schedule status", err);
        } finally {
            setTogglingId(null);
        }
    };

    const renderRecipients = (recipients: string[]) => {
        if (recipients.length === 0) {
            return <span className="text-muted">No recipients</span>;
        }

        const shown = recipients.slice(0, maxRecipientsShown);
        const remaining = recipients.length - shown.length;

        return (
            <RecipientsList>
                {shown.map((email, index) => (
                    <div key={index}>{email}</div>
                ))}
                {remaining > 0 && (
                    <div className="more-recipients">+{remaining} more</div>
                )}
            </RecipientsList>
        );
    };

    if (isLoading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2 text-muted">Loading schedules...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger">
                {error}
            </Alert>
        );
    }

    if (schedules.length === 0) {
        return (
            <EmptyState>
                <h5>No Scheduled Exports</h5>
                <p>You haven&apos;t created any scheduled exports yet.</p>
            </EmptyState>
        );
    }

    return (
        <>
            <StyledTable striped hover responsive>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Format</th>
                        <th>Frequency</th>
                        <th>Recipients</th>
                        <th>Status</th>
                        <th>Last Run</th>
                        <th>Next Run</th>
                        <th className="actions-cell">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {schedules.map((schedule) => (
                        <tr key={schedule.id}>
                            <td>{schedule.exportName}</td>
                            <td>
                                <Badge variant="secondary" className="text-uppercase">
                                    {schedule.format}
                                </Badge>
                            </td>
                            <td>{formatFrequency(schedule)}</td>
                            <td>{renderRecipients(schedule.recipients)}</td>
                            <td>
                                <Badge variant={getStatusVariant(schedule.status)} className="frequency-badge">
                                    {schedule.status}
                                </Badge>
                            </td>
                            <td>
                                {schedule.lastRunAt 
                                    ? moment(schedule.lastRunAt).format("MMM D, YYYY h:mm A")
                                    : <span className="text-muted">Never</span>
                                }
                            </td>
                            <td>
                                {schedule.nextRunAt && schedule.status === "active"
                                    ? moment(schedule.nextRunAt).format("MMM D, YYYY h:mm A")
                                    : <span className="text-muted">—</span>
                                }
                            </td>
                            <td className="actions-cell">
                                {onEdit && (
                                    <ActionButton
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => onEdit(schedule)}
                                        title="Edit"
                                    >
                                        <PencilSquare />
                                    </ActionButton>
                                )}
                                {onToggleStatus && schedule.status !== "deleted" && (
                                    <ActionButton
                                        variant={schedule.status === "active" ? "outline-warning" : "outline-success"}
                                        size="sm"
                                        onClick={() => handleToggleStatus(schedule)}
                                        disabled={togglingId === schedule.id}
                                        title={schedule.status === "active" ? "Pause" : "Resume"}
                                    >
                                        {togglingId === schedule.id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : schedule.status === "active" ? (
                                            <PauseCircle />
                                        ) : (
                                            <PlayCircle />
                                        )}
                                    </ActionButton>
                                )}
                                {onDelete && (
                                    <ActionButton
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => setConfirmDeleteId(schedule.id)}
                                        disabled={deletingId === schedule.id}
                                        title="Delete"
                                    >
                                        {deletingId === schedule.id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <Trash />
                                        )}
                                    </ActionButton>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </StyledTable>

            {/* Delete Confirmation Modal */}
            <Modal show={!!confirmDeleteId} onHide={() => setConfirmDeleteId(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this scheduled export? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmDeleteId(null)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}
                    >
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ScheduledExportsList;
